import axios, { AxiosError, AxiosRequestConfig, AxiosResponse } from 'axios'
import { sleep } from '@/utils/sleep'

const SPOTIFY_API = 'https://api.spotify.com/v1'
const MAX_ATTEMPTS = 3
const TRACKS_PER_REQUEST = 100

export interface CreatePlaylistInput {
  // Name of the playlist to create
  name: string
  // Spotify track ids (not uris), in the order they should appear
  trackIds: string[]
  // Pre-compressed, base64-encoded cover image (optional)
  coverImageBase64?: string | null
}

class SpotifyApi {
  // Retries on rate limiting (429) and transient/5xx errors, honouring the
  // Retry-After header the way the server-side client did.
  private async request (accessToken: string, config: AxiosRequestConfig, attempt = 1): Promise<AxiosResponse> {
    try {
      return await axios({
        ...config,
        headers: {
          Authorization: `Bearer ${accessToken}`,
          ...config.headers,
        },
      })
    } catch (e) {
      const err = e as AxiosError
      const status = err.response?.status
      const retryable = !status || status === 429 || status >= 500
      if (attempt >= MAX_ATTEMPTS || !retryable) {
        throw err
      }
      const retryAfter = Number(err.response?.headers?.['retry-after'])
      const waitMs = Number.isFinite(retryAfter) && retryAfter > 0
        ? (retryAfter + 1) * 1000 // +1 because Spotify rounds down
        : attempt * 500
      await sleep(waitMs)
      return this.request(accessToken, config, attempt + 1)
    }
  }

  private async getCurrentUserId (accessToken: string): Promise<string> {
    const res = await this.request(accessToken, { method: 'GET', url: `${SPOTIFY_API}/me` })
    return res.data.id
  }

  private async createPlaylist (accessToken: string, userId: string, name: string): Promise<string> {
    const res = await this.request(accessToken, {
      method: 'POST',
      url: `${SPOTIFY_API}/users/${userId}/playlists`,
      data: { name, public: true },
    })
    return res.data.id
  }

  private async addTracks (accessToken: string, playlistId: string, trackIds: string[]): Promise<void> {
    const url = `${SPOTIFY_API}/playlists/${playlistId}/tracks`
    for (let i = 0; i < trackIds.length; i += TRACKS_PER_REQUEST) {
      const chunk = trackIds.slice(i, i + TRACKS_PER_REQUEST)
      const uris = chunk.map((id) => `spotify:track:${id}`)
      // First chunk replaces (PUT), the rest append (POST), matching the
      // server's behaviour for playlists longer than 100 tracks.
      await this.request(accessToken, {
        method: i === 0 ? 'PUT' : 'POST',
        url,
        data: { uris },
      })
    }
  }

  private async setCoverImage (accessToken: string, playlistId: string, base64: string): Promise<void> {
    try {
      await this.request(accessToken, {
        method: 'PUT',
        url: `${SPOTIFY_API}/playlists/${playlistId}/images`,
        data: base64,
        headers: { 'Content-Type': 'image/jpeg' },
      })
    } catch (e) {
      // A failed cover upload should not fail playlist creation; the playlist
      // and its tracks already exist. Mirrors the server tolerating this.
      console.error('Failed to set Spotify playlist cover image', e)
    }
  }

  // Creates the playlist on Spotify and returns its id. The account is whoever
  // the access token belongs to (the logged-in user, or the shared account).
  public async createPlaylistWithTracks (accessToken: string, input: CreatePlaylistInput): Promise<string> {
    const userId = await this.getCurrentUserId(accessToken)
    const playlistId = await this.createPlaylist(accessToken, userId, input.name)
    await this.addTracks(accessToken, playlistId, input.trackIds)
    if (input.coverImageBase64) {
      await this.setCoverImage(accessToken, playlistId, input.coverImageBase64)
    }
    return playlistId
  }
}

export default new SpotifyApi()
